import { FC, useState, useEffect, useRef, useCallback } from "react";
import "./slideshow.css";

const INITIAL_MS = 2000;
const FAST_MIN_MS = 150;
const FAST_MAX_MS = 500;
const SLOW_MIN_MS = 1000;
const SLOW_MAX_MS = 1500;
const SLOW_WEIGHT = 0.4; // max base probability of slow (at max scale)
const SLOW_BOOST = 0.5; // max additional boost for slides below average slow count
const BATCH_SIZE = 5;
const FULL_SCREEN_THRESHOLD = 0.9; // scale >= this snaps to 1.0
const MIN_SCALE_SM = 0.75;
const MAX_SCALE_SM = 1.01;
const MIN_SCALE_LG = 0.5;
const MAX_SCALE_LG = 1;
const BREAKPOINT = "(min-width: 800px)";

export interface Slide {
  url: string;
  scale?: number;    // forced scale; overrides random
  minDelay?: number; // minimum display time in ms
}

export interface SlideshowProps {
  slides: Slide[];
}

type Slot = { url: string; scale: number };

const slotStyle = ({ url, scale }: Slot, opacity: number) => ({
  backgroundImage: `url("${url}")`,
  backgroundSize: scale < 1 ? "contain" : "cover",
  opacity,
  transform: `scale(${scale})`,
});

const randomSlideParams = (
  nextUrl: string,
  slideCount: number,
  slowCounts: Map<string, number>,
  totalSlides: number,
  forcedScale?: number,
  minDelay?: number,
) => {
  const lg = window.matchMedia(BREAKPOINT).matches;
  const minScale = lg ? MIN_SCALE_LG : MIN_SCALE_SM;
  const maxScale = lg ? MAX_SCALE_LG : MAX_SCALE_SM;
  const raw = minScale + Math.random() * (maxScale - minScale);
  const scale = forcedScale ?? (raw >= FULL_SCREEN_THRESHOLD ? 1.0 : raw);

  const baseProb = ((raw - minScale) / (maxScale - minScale)) * SLOW_WEIGHT;

  // ramp from 0 → 1 over 2 full cycles
  const ramp = Math.min(1, slideCount / (totalSlides * 2));

  // deficit: 1 if slide has never been slow, 0 if at or above average
  const totalSlow = Array.from(slowCounts.values()).reduce((a, b) => a + b, 0);
  const avgSlow = totalSlow / totalSlides;
  const thisSlow = slowCounts.get(nextUrl) ?? 0;
  const deficit = avgSlow > 0 ? Math.max(0, 1 - thisSlow / avgSlow) : 1;

  const slowProb = Math.min(1, baseProb + SLOW_BOOST * ramp * deficit);
  const slow = Math.random() < slowProb;

  const rawDelay = slow
    ? SLOW_MIN_MS + Math.random() * (SLOW_MAX_MS - SLOW_MIN_MS)
    : FAST_MIN_MS + Math.random() * (FAST_MAX_MS - FAST_MIN_MS);
  const delay = minDelay !== undefined ? Math.max(rawDelay, minDelay) : rawDelay;

  return { scale, delay, slow };
};

export const SlideShow: FC<SlideshowProps> = ({ slides }) => {
  const [index, setIndex] = useState(0);
  const [activeSlot, setActiveSlot] = useState<"a" | "b">("a");
  const [ready, setReady] = useState(false);
  const initial: Slot = { url: slides[0].url, scale: slides[0].scale ?? 1.0 };
  const slots = useRef({ a: initial, b: initial });
  const loadedUrls = useRef(new Set<string>());
  const slowCounts = useRef(new Map<string, number>());
  const slideCount = useRef(0);

  const preloadBatch = useCallback(
    (from: number) => {
      slides.slice(from, from + BATCH_SIZE).forEach(({ url }) => {
        if (loadedUrls.current.has(url)) return;
        const img = new Image();
        img.onload = () => loadedUrls.current.add(url);
        img.src = url;
      });
    },
    [slides],
  );

  // Gate on timer elapsed AND first batch loaded, whichever is longer
  useEffect(() => {
    let timerDone = false;
    let batchDone = false;
    const check = () => {
      if (timerDone && batchDone) setReady(true);
    };

    const t = setTimeout(() => {
      timerDone = true;
      check();
    }, INITIAL_MS);

    let count = 0;
    const total = Math.min(BATCH_SIZE, slides.length - 1);
    if (total === 0) {
      batchDone = true;
    }
    slides.slice(1, 1 + BATCH_SIZE).forEach(({ url }) => {
      const img = new Image();
      const done = () => {
        loadedUrls.current.add(url);
        if (++count >= total) {
          batchDone = true;
          check();
        }
      };
      img.onload = done;
      img.onerror = done;
      img.src = url;
    });

    return () => clearTimeout(t);
  }, []);

  // Advance when ready
  useEffect(() => {
    if (!ready) return;

    if (index > 0 && index % BATCH_SIZE === 0) preloadBatch(index + BATCH_SIZE);

    const nextIndex = (index + 1) % slides.length;
    const nextSlide = slides[nextIndex];
    const { scale, delay, slow } = randomSlideParams(
      nextSlide.url,
      slideCount.current,
      slowCounts.current,
      slides.length,
      nextSlide.scale,
      slides[index].minDelay,
    );
    const nextUrl = nextSlide.url;

    const advance = () => {
      const nextSlot = activeSlot === "a" ? "b" : "a";
      slots.current[nextSlot] = { url: nextUrl, scale };
      if (slow)
        slowCounts.current.set(
          nextUrl,
          (slowCounts.current.get(nextUrl) ?? 0) + 1,
        );
      slideCount.current++;
      setActiveSlot(nextSlot);
      setIndex(nextIndex);
    };

    const t = setTimeout(() => {
      if (loadedUrls.current.has(nextUrl)) {
        advance();
      } else {
        const img = new Image();
        img.onload = () => {
          loadedUrls.current.add(nextUrl);
          advance();
        };
        img.src = nextUrl;
      }
    }, delay);

    return () => clearTimeout(t);
  }, [index, ready, activeSlot, slides.length]);

  return (
    <>
      <div
        className="slide"
        style={slotStyle(slots.current.a, activeSlot === "a" ? 1 : 0)}
      />
      <div
        className="slide"
        style={slotStyle(slots.current.b, activeSlot === "b" ? 1 : 0)}
      />
    </>
  );
};
